import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { Link as RouterLink } from 'react-router-dom'
import { useSimulations } from '../api/hooks'
import SharedBadge from '../components/SharedBadge'
import { formatDate, formatEUR, formatEUR2, formatPct } from '../format'
import { useAmountsHidden } from '../privacy'
import { BorderedIconButton } from '../components/BorderedIconButton'

export default function SimulationsPage() {
  const { data, loading, error } = useSimulations()
  useAmountsHidden()
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down('sm'))

  return (
    <Box>
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 3, justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Typography variant="h4">Simulations</Typography>

        {isMobileView ? (
          <BorderedIconButton
            Icon={AddIcon}
            label="Nouvelle simulation"
            component={RouterLink}
            to="/simulations/new"
          />
        ) : (
          <Button
            component={RouterLink}
            to="/simulations/new"
            variant="contained"
            startIcon={<AddIcon />}
          >
            Nouvelle simulation
          </Button>
        )}
      </Stack>

      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}

      {data && data.length === 0 && (
        <Card>
          <CardContent>
            <Typography sx={{ color: 'text.secondary' }}>
              Aucune simulation. Créez votre première simulation pour estimer la rentabilité d'un
              bien.
            </Typography>
          </CardContent>
        </Card>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
        }}
      >
        {data?.map((s) => (
          <Card key={s.id}>
            <CardActionArea
              component={RouterLink}
              to={`/simulations/${s.id}`}
              sx={{ height: '100%' }}
            >
              <CardContent>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
                >
                  <Typography variant="h6" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    {s.name}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    useFlexGap
                    sx={{ flexWrap: 'wrap', justifyContent: 'flex-end', flexShrink: 0 }}
                  >
                    <Chip
                      size="small"
                      label={s.project ? 'Actif' : 'Brouillon'}
                      color={s.project ? 'success' : 'default'}
                    />
                    <SharedBadge
                      role={s.role}
                      ownerEmail={s.ownerEmail}
                      sharedWithOthers={s.sharedWithOthers}
                    />
                  </Stack>
                </Stack>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {formatDate(s.createdAt)}
                </Typography>
                {!s.isOwner && s.ownerEmail && (
                  <Typography variant="body2" sx={{ color: 'info.main', overflowWrap: 'anywhere' }}>
                    Partagé par {s.ownerEmail}
                  </Typography>
                )}
                <Stack direction="row" sx={{ mt: 2, justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Cash-flow
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: s.results.monthlyCashFlow >= 0 ? 'success.main' : 'error.main' }}
                  >
                    {formatEUR2(s.results.monthlyCashFlow)}/mois
                  </Typography>
                </Stack>
                <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Rendement net
                  </Typography>
                  <Typography variant="body2">{formatPct(s.results.netYield)}</Typography>
                </Stack>
                <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Coût de l'investissement
                  </Typography>
                  <Typography variant="body2">{formatEUR(s.results.investmentCost)}</Typography>
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  )
}
